import { FormArray, FormGroup } from '@angular/forms';
import { TipoFirmante } from '../../envios/models/tipo-firmante';
import { ContenidoDocVisible } from '../models/contenido-doc-visible';
import { CasillaDibujo, FirmaDibujo } from '../models/firmante-nif';
import { TiposContenido } from '../models/tipos-contenido';
import { MedidaPdf, normalizedRectToPixel, pixelRectToNormalized, RectPixel } from './formulario-documento-coordenadas';

export type OverlayDocumentoTipo = 'casilla' | 'campo' | 'firma' | 'palabra';

export interface UpdateCasillaPositionPatch {
  page?: number;
  nombre?: string;
  orden?: number;
  valorOpcionSeleccionada?: string;
  obligatorio?: boolean;
  idNombreFormulario?: number | null;
}

export interface CasillaControlLocation {
  firmanteIndex: number;
  casillaIndex: number;
  group: FormGroup;
  tipoFirmante: TipoFirmante;
}

export interface CampoControlLocation {
  firmanteIndex: number;
  campoIndex: number;
  group: FormGroup;
  tipoFirmante: TipoFirmante;
}

export interface FirmaControlLocation {
  firmanteIndex: number;
  firmaIndex: number;
  group: FormGroup;
  tipoFirmante: TipoFirmante;
}

export interface PalabraControlLocation {
  palabraIndex: number;
  group: FormGroup;
}

export interface UpdatePalabraPositionPatch {
  page?: number;
  tipo?: TiposContenido;
  identificador?: string;
  nombre?: string;
}

export interface UpdateCampoPositionPatch {
  page?: number;
  identificador?: string;
  nombreCampo?: string;
  valorPorDefecto?: string;
  obligatorio?: boolean;
}

function isOverlayRowColocada(rowGroup: FormGroup, medida?: MedidaPdf | null): boolean {
  const pagina = rowGroup.get('pagina')?.value;
  if (pagina == null) {
    return false;
  }

  const posicionTop = rowGroup.get('posicionTop')?.value;
  const posicionLeft = rowGroup.get('posicionLeft')?.value;
  const posicionWidth = Number(rowGroup.get('posicionWidth')?.value);
  const posicionHeight = Number(rowGroup.get('posicionHeight')?.value);

  if (
    posicionTop == null ||
    posicionLeft == null ||
    !Number.isFinite(posicionWidth) ||
    posicionWidth <= 0 ||
    !Number.isFinite(posicionHeight) ||
    posicionHeight <= 0
  ) {
    return false;
  }

  if (medida) {
    const pixel = normalizedRectToPixel(
      { posicionTop, posicionLeft, posicionWidth, posicionHeight },
      medida
    );
    return hasValidPixelRect(pixel.x, pixel.y, pixel.width, pixel.height);
  }

  return true;
}

export function isCasillaRowColocada(casillaGroup: FormGroup, medida?: MedidaPdf | null): boolean {
  return isOverlayRowColocada(casillaGroup, medida);
}

export const isCampoRowColocada = isCasillaRowColocada;
export const isFirmaRowColocada = isCasillaRowColocada;
export const isPalabraRowColocada = isCasillaRowColocada;

function hasValidPixelRect(x: number, y: number, width: number, height: number): boolean {
  return (
    !isNaN(x) &&
    !isNaN(y) &&
    !isNaN(width) &&
    width > 0 &&
    !isNaN(height) &&
    height > 0
  );
}

export function locateCasillaControl(
  firmantesNif: FormArray,
  id: number,
  firmanteCodigo: string
): CasillaControlLocation | null {
  for (let firmanteIndex = 0; firmanteIndex < firmantesNif.length; firmanteIndex++) {
    const firmanteGroup = firmantesNif.at(firmanteIndex) as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (tipoFirmante?.codigo !== firmanteCodigo) {
      continue;
    }
    const formularios = firmanteGroup.get('formularios') as FormArray;
    if (!formularios) {
      continue;
    }
    for (let casillaIndex = 0; casillaIndex < formularios.length; casillaIndex++) {
      const casillaGroup = formularios.at(casillaIndex) as FormGroup;
      if (casillaGroup.get('id')?.value === id) {
        return { firmanteIndex, casillaIndex, group: casillaGroup, tipoFirmante };
      }
    }
  }
  return null;
}

export function projectCasillasFromForm(firmantesNif: FormArray, medida: MedidaPdf, page?: number, firmante?: TipoFirmante | null): CasillaDibujo[] {
  const overlays: CasillaDibujo[] = [];

  firmantesNif.controls.forEach((firmanteCtrl) => {
    const firmanteGroup = firmanteCtrl as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (firmante && tipoFirmante?.codigo !== firmante.codigo) {
      return;
    }

    const formularios = firmanteGroup.get('formularios') as FormArray;
    if (!formularios) {
      return;
    }

    formularios.controls.forEach((casillaCtrl) => {
      const casillaGroup = casillaCtrl as FormGroup;
      if (!isCasillaRowColocada(casillaGroup, medida)) {
        return;
      }
      const pagina = casillaGroup.get('pagina')?.value;
      if (page != null && pagina !== page) {
        return;
      }

      const pixel = normalizedRectToPixel(
        {
          posicionTop: casillaGroup.get('posicionTop')?.value,
          posicionLeft: casillaGroup.get('posicionLeft')?.value,
          posicionWidth: casillaGroup.get('posicionWidth')?.value,
          posicionHeight: casillaGroup.get('posicionHeight')?.value,
        },
        medida
      );

      if (!hasValidPixelRect(pixel.x, pixel.y, pixel.width, pixel.height)) {
        return;
      }

      overlays.push({
        id: casillaGroup.get('id')?.value,
        pagina,
        x: pixel.x,
        y: pixel.y,
        width: pixel.width,
        height: pixel.height,
        firmante: tipoFirmante,
        orden: casillaGroup.get('orden')?.value,
        nombre: casillaGroup.get('nombre')?.value,
        valorOpcionSeleccionada: casillaGroup.get('valorOpcionSeleccionada')?.value,
        obligatorio: casillaGroup.get('obligatorio')?.value,
        idNombreFormulario: casillaGroup.get('idNombreFormulario')?.value,
      });
    });
  });

  return overlays;
}

export function locateCampoControl(
  firmantesNif: FormArray,
  id: number,
  firmanteCodigo: string
): CampoControlLocation | null {
  for (let firmanteIndex = 0; firmanteIndex < firmantesNif.length; firmanteIndex++) {
    const firmanteGroup = firmantesNif.at(firmanteIndex) as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (tipoFirmante?.codigo !== firmanteCodigo) {
      continue;
    }
    const camposEditables = firmanteGroup.get('camposEditables') as FormArray;
    if (!camposEditables) {
      continue;
    }
    for (let campoIndex = 0; campoIndex < camposEditables.length; campoIndex++) {
      const campoGroup = camposEditables.at(campoIndex) as FormGroup;
      if (campoGroup.get('id')?.value === id) {
        return { firmanteIndex, campoIndex, group: campoGroup, tipoFirmante };
      }
    }
  }
  return null;
}

export function projectCamposFromForm(
  firmantesNif: FormArray,
  medida: MedidaPdf,
  page?: number,
  firmante?: TipoFirmante | null
): CasillaDibujo[] {
  const overlays: CasillaDibujo[] = [];

  firmantesNif.controls.forEach((firmanteCtrl) => {
    const firmanteGroup = firmanteCtrl as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (firmante && tipoFirmante?.codigo !== firmante.codigo) {
      return;
    }

    const camposEditables = firmanteGroup.get('camposEditables') as FormArray;
    if (!camposEditables) {
      return;
    }

    camposEditables.controls.forEach((campoCtrl) => {
      const campoGroup = campoCtrl as FormGroup;
      if (!isCampoRowColocada(campoGroup, medida)) {
        return;
      }
      const pagina = campoGroup.get('pagina')?.value;
      if (page != null && pagina !== page) {
        return;
      }

      const pixel = normalizedRectToPixel(
        {
          posicionTop: campoGroup.get('posicionTop')?.value,
          posicionLeft: campoGroup.get('posicionLeft')?.value,
          posicionWidth: campoGroup.get('posicionWidth')?.value,
          posicionHeight: campoGroup.get('posicionHeight')?.value,
        },
        medida
      );

      if (!hasValidPixelRect(pixel.x, pixel.y, pixel.width, pixel.height)) {
        return;
      }

      overlays.push({
        id: campoGroup.get('id')?.value,
        pagina,
        x: pixel.x,
        y: pixel.y,
        width: pixel.width,
        height: pixel.height,
        firmante: tipoFirmante,
        orden: 0,
        nombre: campoGroup.get('nombreCampo')?.value ?? '',
        identificador: campoGroup.get('identificador')?.value,
        nombreCampo: campoGroup.get('nombreCampo')?.value,
        valorPorDefecto: campoGroup.get('valorPorDefecto')?.value,
        obligatorio: campoGroup.get('obligatorio')?.value,
      });
    });
  });

  return overlays;
}

export function locateFirmaControl(
  firmantesNif: FormArray,
  id: number,
  firmanteCodigo: string
): FirmaControlLocation | null {
  for (let firmanteIndex = 0; firmanteIndex < firmantesNif.length; firmanteIndex++) {
    const firmanteGroup = firmantesNif.at(firmanteIndex) as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (tipoFirmante?.codigo !== firmanteCodigo) {
      continue;
    }
    const posicionFirmas = firmanteGroup.get('firmas')?.get('posicionFirmas') as FormArray;
    if (!posicionFirmas) {
      continue;
    }
    for (let firmaIndex = 0; firmaIndex < posicionFirmas.length; firmaIndex++) {
      const firmaGroup = posicionFirmas.at(firmaIndex) as FormGroup;
      if (firmaGroup.get('id')?.value === id) {
        return { firmanteIndex, firmaIndex, group: firmaGroup, tipoFirmante };
      }
    }
  }
  return null;
}

export function projectFirmasFromForm(
  firmantesNif: FormArray,
  medida: MedidaPdf,
  page?: number,
  firmante?: TipoFirmante | null
): FirmaDibujo[] {
  const overlays: FirmaDibujo[] = [];

  firmantesNif.controls.forEach((firmanteCtrl) => {
    const firmanteGroup = firmanteCtrl as FormGroup;
    const tipoFirmante = firmanteGroup.get('tipoFirmante')?.value as TipoFirmante;
    if (firmante && tipoFirmante?.codigo !== firmante.codigo) {
      return;
    }

    const posicionFirmas = firmanteGroup.get('firmas')?.get('posicionFirmas') as FormArray;
    if (!posicionFirmas) {
      return;
    }

    posicionFirmas.controls.forEach((firmaCtrl) => {
      const firmaGroup = firmaCtrl as FormGroup;
      if (!isFirmaRowColocada(firmaGroup, medida)) {
        return;
      }
      const pagina = firmaGroup.get('pagina')?.value;
      if (page != null && pagina !== page) {
        return;
      }

      const pixel = normalizedRectToPixel(
        {
          posicionTop: firmaGroup.get('posicionTop')?.value,
          posicionLeft: firmaGroup.get('posicionLeft')?.value,
          posicionWidth: firmaGroup.get('posicionWidth')?.value,
          posicionHeight: firmaGroup.get('posicionHeight')?.value,
        },
        medida
      );

      if (!hasValidPixelRect(pixel.x, pixel.y, pixel.width, pixel.height)) {
        return;
      }

      overlays.push({
        id: firmaGroup.get('id')?.value,
        pagina,
        x: pixel.x,
        y: pixel.y,
        width: pixel.width,
        height: pixel.height,
        firmante: (firmaGroup.get('firmante')?.value as TipoFirmante) ?? tipoFirmante,
      });
    });
  });

  return overlays;
}

export function locatePalabraControl(tiposContenido: FormArray, id: number): PalabraControlLocation | null {
  for (let palabraIndex = 0; palabraIndex < tiposContenido.length; palabraIndex++) {
    const palabraGroup = tiposContenido.at(palabraIndex) as FormGroup;
    if (palabraGroup.get('id')?.value === id) {
      return { palabraIndex, group: palabraGroup };
    }
  }
  return null;
}

export function projectPalabrasFromForm(
  tiposContenido: FormArray,
  medida: MedidaPdf,
  page?: number
): ContenidoDocVisible[] {
  const overlays: ContenidoDocVisible[] = [];

  tiposContenido.controls.forEach((palabraCtrl) => {
    const palabraGroup = palabraCtrl as FormGroup;
    if (!isPalabraRowColocada(palabraGroup, medida)) {
      return;
    }
    const pagina = palabraGroup.get('pagina')?.value;
    if (page != null && pagina !== page) {
      return;
    }

    const pixel = normalizedRectToPixel(
      {
        posicionTop: palabraGroup.get('posicionTop')?.value,
        posicionLeft: palabraGroup.get('posicionLeft')?.value,
        posicionWidth: palabraGroup.get('posicionWidth')?.value,
        posicionHeight: palabraGroup.get('posicionHeight')?.value,
      },
      medida
    );

    if (!hasValidPixelRect(pixel.x, pixel.y, pixel.width, pixel.height)) {
      return;
    }

    overlays.push({
      id: palabraGroup.get('id')?.value,
      pagina,
      tipo: palabraGroup.get('tipo')?.value as TiposContenido,
      x: pixel.x,
      y: pixel.y,
      width: pixel.width,
      height: pixel.height,
      identificador: palabraGroup.get('identificador')?.value ?? '',
      nombre: palabraGroup.get('nombre')?.value ?? '',
    });
  });

  return overlays;
}

export function normalizedCoordsFromPixelRect(rect: RectPixel, medida: MedidaPdf) {
  return pixelRectToNormalized(rect, medida);
}
